/**
 * @author        luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright     Copyright (c) 2023, Zumper
 */
import { Box, Text } from '@chakra-ui/react'
import { getUnitTitle } from '@zcode-ui/perm-address'
import { InputWithPopover } from '@zcode-ui/web-zumper-ui'
import type { Listing, MinPAListingData } from '@zcode/state-type'
import { dollarize } from '@zcode/utils'
import React, { useMemo } from 'react'

interface Props {
  error?: string
  listings: ReadonlyArray<Listing<MinPAListingData>>
  isDefault?: boolean
  isInvalid?: boolean
  isMobile: boolean
  isRequired?: boolean
  name?: string
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  selectedListing?: Listing | null
}

const getTitleAndPrice = (option: Listing<MinPAListingData>) => {
  const { isActive, price } = option
  const title = getUnitTitle(option)
  const priceText = price && isActive ? dollarize(price) : 'Ask to join waitlist'
  return { title, priceText }
}

const getOptionLabel = (option: Listing<MinPAListingData>) => {
  const { priceText, title } = getTitleAndPrice(option)

  return (
    <Box<'div'> display="flex" justifyContent="space-between">
      <Text variant="body14Md">{title}</Text>
      <Text color="gray.600" variant="body14Md">
        {priceText}
      </Text>
    </Box>
  )
}

const getDisplayValue = (option?: Listing<MinPAListingData>) => {
  if (!option) return ''
  const { priceText, title } = getTitleAndPrice(option)

  return [title, priceText].join(' ')
}

const getOption = (option: Listing<MinPAListingData>) => {
  return {
    label: getOptionLabel(option),
    value: option.id,
    displayValue: getDisplayValue(option),
  } as const
}

export const UnitInput = ({
  error,
  isDefault,
  isInvalid,
  isMobile,
  isRequired,
  listings,
  name = 'selectedListingId',
  onChange,
  selectedListing,
}: Props) => {
  const options = useMemo(() => listings.map((listing) => getOption(listing)), [listings])
  const value = useMemo(() => {
    if (!selectedListing) {
      return listings[0]
    }

    return listings.find((listing) => listing.id === selectedListing.id)
  }, [selectedListing, listings])
  return (
    <InputWithPopover
      error={error}
      inputLabel="Unit"
      inputProps={{
        width: '100%',
      }}
      isDefault={isDefault}
      isInvalid={isInvalid}
      isMobile={isMobile}
      isRequired={isRequired}
      name={name}
      onChange={onChange}
      placement="bottom-end"
      popoverHeader="Unit"
      popoverItems={options}
      value={getDisplayValue(value)}
    />
  )
}
